/**
 * CardContainerEditor
 *
 * Property editor for card-container control type
 * Configures card styling, shadows, spacing, and content layout
 *
 * SRP: Responsible ONLY for configuring card-container properties
 * Control Type: 'card-container'
 *
 * Properties Configured (8):
 * 1. title - Card title text
 * 2. backgroundColor - Card background color
 * 3. borderColor - Card border color
 * 4. borderRadius - Corner radius (px)
 * 5. shadowLevel - Shadow depth (0-3)
 * 6. padding - Inner spacing (px)
 * 7. borderWidth - Border thickness (px)
 * 8. elevation - Z-index elevation
 */

import React, { useCallback, useMemo } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';
import { TextPropertyEditor } from '../BaseEditors/TextPropertyEditor';
import { ColorPropertyEditor } from '../BaseEditors/ColorPropertyEditor';
import { NumberPropertyEditor } from '../BaseEditors/NumberPropertyEditor';
import { SelectPropertyEditor } from '../BaseEditors/SelectPropertyEditor';

/**
 * Card container configuration
 */
interface CardContainerConfig {
  title?: string;
  backgroundColor?: string;
  borderColor?: string;
  borderRadius?: number;
  shadowLevel?: number;
  padding?: number;
  borderWidth?: number;
  elevation?: number;
}

/**
 * CardContainerEditor Component
 * Renders property editors for all card-container configuration
 */
const CardContainerEditorComponent: React.FC<PropertyEditorProps> = ({
  control,
  onConfigChange,
  onBasicChange,
}) => {
  // Memoize config with type safety
  const config = useMemo(
    () => (control.config as CardContainerConfig) || {},
    [control.config],
  );

  // Handler creators with useCallback
  const handleTitleChange = useCallback(
    (value: string) => onConfigChange('title', value),
    [onConfigChange],
  );

  const handleBackgroundColorChange = useCallback(
    (value: string) => onConfigChange('backgroundColor', value),
    [onConfigChange],
  );

  const handleBorderColorChange = useCallback(
    (value: string) => onConfigChange('borderColor', value),
    [onConfigChange],
  );

  const handleBorderRadiusChange = useCallback(
    (value: number) => onConfigChange('borderRadius', value),
    [onConfigChange],
  );

  const handleShadowLevelChange = useCallback(
    (value: string) => onConfigChange('shadowLevel', parseInt(value, 10)),
    [onConfigChange],
  );

  const handlePaddingChange = useCallback(
    (value: number) => onConfigChange('padding', value),
    [onConfigChange],
  );

  const handleBorderWidthChange = useCallback(
    (value: number) => onConfigChange('borderWidth', value),
    [onConfigChange],
  );

  const handleElevationChange = useCallback(
    (value: number) => onConfigChange('elevation', value),
    [onConfigChange],
  );

  const shadowOptions = useMemo(
    () => [
      { label: 'None', value: '0' },
      { label: 'Small', value: '1' },
      { label: 'Medium', value: '2' },
      { label: 'Large', value: '3' },
    ],
    [],
  );

  return (
    <div className="af-property-editor__section card-container-editor">
      <h3 className="af-property-editor__section-title">Card Container Properties</h3>

      <TextPropertyEditor
        label="Card Title"
        value={config.title || ''}
        onChange={handleTitleChange}
        placeholder="Enter card title"
        helpText="Display title for the card container"
      />

      <ColorPropertyEditor
        label="Background Color"
        value={config.backgroundColor || '#FFFFFF'}
        onChange={handleBackgroundColorChange}
        helpText="Card background color"
        showRgb={false}
      />

      <ColorPropertyEditor
        label="Border Color"
        value={config.borderColor || '#E0E0E0'}
        onChange={handleBorderColorChange}
        helpText="Card border color"
      />

      <NumberPropertyEditor
        label="Border Radius (px)"
        value={config.borderRadius || 4}
        onChange={handleBorderRadiusChange}
        min={0}
        max={100}
        step={1}
        helpText="Corner radius in pixels"
      />

      <SelectPropertyEditor
        label="Shadow Level"
        value={String(config.shadowLevel || 0)}
        onChange={handleShadowLevelChange}
        options={shadowOptions}
        helpText="Depth of card shadow"
      />

      <NumberPropertyEditor
        label="Padding (px)"
        value={config.padding || 16}
        onChange={handlePaddingChange}
        min={0}
        max={100}
        step={4}
        helpText="Inner spacing in pixels"
      />

      <NumberPropertyEditor
        label="Border Width (px)"
        value={config.borderWidth || 1}
        onChange={handleBorderWidthChange}
        min={0}
        max={10}
        step={1}
        helpText="Border thickness in pixels"
      />

      <NumberPropertyEditor
        label="Elevation (Z-Index)"
        value={config.elevation || 1}
        onChange={handleElevationChange}
        min={0}
        max={1000}
        step={1}
        helpText="Stacking order (z-index)"
      />
    </div>
  );
};

CardContainerEditorComponent.displayName = 'CardContainerEditor';

/**
 * Card Container Property Editor Registration
 * Exported as PropertyEditor interface for registry
 */
export const CardContainerEditor: PropertyEditor = {
  type: 'card-container',
  label: 'Card Container',
  description: 'Configure card styling, shadows, spacing, and elevation',
  version: '1.0.0',
  render: (props) => <CardContainerEditorComponent {...props} />,
};

export default CardContainerEditor;
